import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import type { UserPreferences, CafeWithAmenities, NoiseLevel } from "@/types/cafe";
import {
  isMustHave,
  classifyParking,
  PARKING_PARTIAL_MULTIPLIER,
} from "@/lib/importanceScale";

export function useUserPreferences() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["user-preferences", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("user_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Failed to fetch user preferences:", error);
        return null;
      }

      return data as UserPreferences | null;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// The For You score lives in one place, shared with the suggested-picks-grades function.
export { calculatePersonalizationScore } from "../../supabase/functions/_shared/picksRanking.ts";


/**
 * Get preference match reasons for display.
 * Only columns the user marked "must have" (3) produce a reason; null columns
 * and legacy out-of-scale values never do.
 */
export function getPreferenceMatchReasons(
  cafe: CafeWithAmenities,
  preferences: UserPreferences | null
): string[] {
  if (!preferences) return [];

  const reasons: string[] = [];

  // Check WiFi match for must-have users
  if (isMustHave(preferences.wifi_importance) && cafe.amenities?.has_wifi) {
    reasons.push("Fast WiFi");
  }

  // Check outlet match
  if (isMustHave(preferences.outlet_importance) &&
      cafe.amenities?.outlet_availability &&
      cafe.amenities.outlet_availability !== "none" &&
      cafe.amenities.outlet_availability !== "unknown") {
    reasons.push("Outlets available");
  }

  // Noise preference matching disabled - no reliable noise data yet

  // Check seating capacity
  if (isMustHave(preferences.seating_comfort_importance)) {
    if (cafe.amenities?.seating_capacity === 'large') {
      reasons.push("Lots of seating");
    } else if (cafe.amenities?.seating_capacity === 'medium') {
      reasons.push("Decent seating");
    }
  }

  // Check food
  if (isMustHave(preferences.food_importance) && cafe.amenities?.menu_type === "full_food") {
    reasons.push("Full menu");
  }

  // Check parking
  if (
    isMustHave((preferences as any).parking_importance) &&
    classifyParking(cafe.amenities?.parking_available) === "free"
  ) {
    reasons.push("Free parking");
  }



  return reasons.slice(0, 3); // Max 3 reasons to display
}
